import { useCallback, useEffect, useRef, useState } from 'react';
import { DESIGN_EXTRA_TYPES, blockDefaults, blockRegistry, type DesignExtra } from '@sarnia/shared';
import { Icon } from '../../../components/ui/Icon';
import InspectorFields from '../../inspectorFields';
import type { PanelCtx } from '../panelTypes';
import './panels.css';

const MAX_EXTRAS = 12;

function newId(): string {
  return `x${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

function sectionLabel(raw: string | undefined): string {
  const label = (raw ?? '').trim();
  if (!label) return 'Section';
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** "Add your own sections": drop any of a set of ready-made sections (text, steps, prices, FAQ...) anywhere on a designed page. */
export default function ExtrasPanel({ ctx }: { ctx: PanelCtx }) {
  const extras: DesignExtra[] = ctx.props.extras ?? [];
  const sections = ctx.entry.sections;
  const [openId, setOpenId] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    []
  );

  /** Typing refreshes the preview a moment after you stop; adding or removing refreshes straight away. */
  const refresh = useCallback(
    (soon: boolean) => {
      if (timer.current !== null) window.clearTimeout(timer.current);
      if (!soon) {
        ctx.reload();
        return;
      }
      timer.current = window.setTimeout(() => {
        timer.current = null;
        ctx.reload();
      }, 600);
    },
    [ctx]
  );

  const save = (next: DesignExtra[], soon: boolean) => {
    ctx.update({ extras: next });
    refresh(soon);
  };

  const add = (type: string) => {
    if (extras.length >= MAX_EXTRAS) return;
    const defaults = (blockDefaults as unknown as Record<string, unknown>)[type];
    if (!defaults) return;
    // Land just above the last section (usually the footer), where an extra section reads naturally.
    const after = Math.max(0, sections.length - 2);
    const extra: DesignExtra = { id: newId(), type, props: structuredClone(defaults) as Record<string, unknown>, after };
    setOpenId(extra.id);
    save([...extras, extra], false);
  };

  const patch = (id: string, change: Partial<DesignExtra>, soon: boolean) => {
    save(
      extras.map((e) => (e.id === id ? { ...e, ...change } : e)),
      soon
    );
  };

  const remove = (id: string) => {
    if (openId === id) setOpenId(null);
    save(
      extras.filter((e) => e.id !== id),
      false
    );
  };

  return (
    <section className="dwp-extras" aria-label="Add your own sections">
      <h3 className="dwp-subheading">Add your own sections</h3>
      <p className="dwp-help">
        Add more to your page: extra text, prices, steps, questions and more. They pick up this template&apos;s colours and fonts.
      </p>

      <div className="dwp-extra-palette" role="group" aria-label="Section types">
        {DESIGN_EXTRA_TYPES.map((type) => (
          <button
            key={type}
            type="button"
            className="dwp-extra-add"
            disabled={extras.length >= MAX_EXTRAS}
            onClick={() => add(type)}
          >
            <Icon name="plus" size={14} />
            <span>{blockRegistry[type]?.label ?? type}</span>
          </button>
        ))}
      </div>
      {extras.length >= MAX_EXTRAS ? <p className="dwp-help">That is the most sections you can add to one page.</p> : null}

      {extras.length > 0 ? (
        <ul className="dwp-extra-list">
          {extras.map((extra) => {
            const def = blockRegistry[extra.type as keyof typeof blockRegistry];
            const open = openId === extra.id;
            return (
              <li key={extra.id} className={`dwp-extra${open ? ' is-open' : ''}`}>
                <div className="dwp-extra-head">
                  <button
                    type="button"
                    className="dwp-extra-toggle"
                    aria-expanded={open}
                    onClick={() => setOpenId(open ? null : extra.id)}
                  >
                    <Icon name="chevron-down" size={16} style={{ transform: open ? 'rotate(180deg)' : undefined }} />
                    <span>{def?.label ?? extra.type}</span>
                  </button>
                  <button
                    type="button"
                    className="dwp-icon-btn"
                    aria-label={`Remove ${def?.label ?? extra.type}`}
                    onClick={() => remove(extra.id)}
                  >
                    <Icon name="trash" size={16} />
                  </button>
                </div>
                {open ? (
                  <div className="dwp-extra-body">
                    <label className="inspector-row">
                      <span className="inspector-label">Place under</span>
                      <select
                        value={extra.after}
                        onChange={(event) => patch(extra.id, { after: Number(event.target.value) }, false)}
                      >
                        {sections.map((section, index) => (
                          <option key={index} value={index}>
                            {sectionLabel(section.label)}
                          </option>
                        ))}
                      </select>
                    </label>
                    {(def?.inspector ?? []).map((field) => (
                      <InspectorFields
                        key={field.key}
                        blockId={extra.id}
                        props={extra.props}
                        field={field}
                        onChange={(change) => patch(extra.id, { props: { ...extra.props, ...change } }, true)}
                      />
                    ))}
                    <label className="inspector-row">
                      <span className="inspector-label">Background</span>
                      <select
                        value={extra.style?.background ?? 'none'}
                        onChange={(event) => {
                          const value = event.target.value;
                          const style = { ...(extra.style ?? {}) } as NonNullable<DesignExtra['style']>;
                          if (value === 'none') delete style.background;
                          else style.background = value as 'surface' | 'tint' | 'accent';
                          patch(extra.id, { style: Object.keys(style).length ? style : undefined }, false);
                        }}
                      >
                        <option value="none">Page colour</option>
                        <option value="surface">Panel</option>
                        <option value="tint">Soft tint</option>
                        <option value="accent">Bold brand colour</option>
                      </select>
                    </label>
                    <label className="inspector-row">
                      <span className="inspector-label">Spacing</span>
                      <select
                        value={extra.style?.space ?? 'normal'}
                        onChange={(event) => {
                          const value = event.target.value;
                          const style = { ...(extra.style ?? {}) } as NonNullable<DesignExtra['style']>;
                          if (value === 'normal') delete style.space;
                          else style.space = value as 'compact' | 'roomy';
                          patch(extra.id, { style: Object.keys(style).length ? style : undefined }, false);
                        }}
                      >
                        <option value="compact">Compact</option>
                        <option value="normal">Normal</option>
                        <option value="roomy">Roomy</option>
                      </select>
                    </label>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}
